// Wave 14+ / Rapha Fit Track B — remeasure history sparkline.
//
// Pulls /fit/history (newest-first list of past measurements) and
// renders a stacked set of sparklines, one per measurement axis
// (chest, waist, hip, thigh). Each line trends from oldest (left) to
// most recent (right). Tooltip on hover shows the date + value.

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme';

interface HistoryRow {
  id: string;
  measuredAt: string;
  heightCm: number | null;
  chestCm: number | null;
  waistCm: number | null;
  hipCm: number | null;
  thighCm: number | null;
}

const AXES: Array<{ key: keyof HistoryRow; labelKey: string; color: string }> = [
  { key: 'chestCm', labelKey: 'labels.chest', color: '#6aff45' },
  { key: 'waistCm', labelKey: 'labels.waist', color: '#6affff' },
  { key: 'hipCm', labelKey: 'labels.hip', color: '#ffd166' },
  { key: 'thighCm', labelKey: 'labels.thigh', color: '#ff7ad9' },
];

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

export default function FitHistory() {
  const { t } = useTranslation('rapha-fit');
  const [rows, setRows] = useState<HistoryRow[]>([]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${API}/fit/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: HistoryRow[]) => setRows(data ?? []))
      .catch(() => {});
  }, []);

  // History comes newest-first; we want oldest-first for left-to-right
  // trend rendering.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.measuredAt < b.measuredAt ? -1 : 1)),
    [rows],
  );

  if (sorted.length < 2) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography sx={{ color: colors.textMuted, fontSize: '0.875rem' }}>
          {t('history.notEnough')}
        </Typography>
      </Box>
    );
  }

  const lastMeasured = sorted[sorted.length - 1].measuredAt;

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
          {t('history.title')}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.textFaint }}>
          {t('history.lastMeasured', { date: new Date(lastMeasured).toLocaleDateString() })}
        </Typography>
      </Stack>
      <Stack spacing={2}>
        {AXES.map((axis) => {
          const points = sorted
            .map((r) => r[axis.key] as number | null)
            .filter((v): v is number => v != null);
          if (points.length < 2) return null;
          return (
            <Box key={axis.key as string} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  minWidth: 80,
                }}
              >
                {t(axis.labelKey)}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Sparkline points={points} color={axis.color} />
              </Box>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.8125rem',
                  color: colors.text,
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {points[points.length - 1].toFixed(1)} cm
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function Sparkline({ points, color }: { points: number[]; color: string }) {
  const W = 320;
  const H = 36;
  const minV = Math.min(...points);
  const maxV = Math.max(...points);
  const span = Math.max(0.5, maxV - minV);
  const path = points
    .map((v, i) => {
      const x = (i / (points.length - 1)) * W;
      const y = ((maxV - v) / span) * H;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      <path d={path} stroke={color} strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={W}
        cy={((maxV - points[points.length - 1]) / span) * H}
        r={3}
        fill={color}
      />
    </svg>
  );
}
